import { request } from "../../../services/api"
import { announcesApprovalChange } from "../../../redux/notificationCounts/approvals-changed.ts"
import type { BaseApiResponse } from "../../../types/api.ts"
import { createFormData } from "../../../utils/form-data/create-form-data"
import { createFormDataNested } from "../../../utils/form-data/create-form-data-nested"
import type { LeadContract } from "../../Lead/types/lead.documents.types.ts"
import type { ContractRequestData } from "../redux/contracts.thunks.types.ts"
import type { ContractFilterOptions } from "../redux/contracts.types.ts"
import type { Contract, ContractListItem } from "../types/types.ts"
import type {
  CancelContractData,
  GetContractsParams,
  RejectContractData,
  SubmitForReviewData,
  UploadContractData,
  UploadContractOptions
} from "./services.types.ts"
import { afterStatusWrite } from "../../StatusAutomation/helpers/automatic-move-events.ts"

const BASE_URL = "/proposal/contracts"

const PATHS = {
  getContracts: `${BASE_URL}/`,
  getPendingContracts: `${BASE_URL}/pending-review/`,
  submitForReview: (id: string) => `${BASE_URL}/${id}/submit-for-review/`,
  getContractInitialValues: (leadID: string) =>
    `/proposal/initial/?lead_id=${leadID}`,
  createContract: `${BASE_URL}/`,
  previewContract: `${BASE_URL}/preview/`,
  generateContract: `${BASE_URL}/generate/`,
  uploadCompanyLogo: `${BASE_URL}/upload-logo/`,
  uploadContractFile: `${BASE_URL}/create-with-file/`,
  filterOptions: `${BASE_URL}/filter_options/`,
  contract: (id: string) => ({
    get: `${BASE_URL}/${id}/`,
    update: `${BASE_URL}/${id}/`,
    save: `${BASE_URL}/${id}/save-file/`,
    reject: `${BASE_URL}/${id}/decline/`,
    cancel: `${BASE_URL}/${id}/cancel/`,
    approve: `${BASE_URL}/${id}/approve/`,
    duplicate: `${BASE_URL}/${id}/create-new-version/`,
    sentToClient: `${BASE_URL}/${id}/send/`,
    sendEmail: `${BASE_URL}/${id}/send-email/`
  })
}

export const getContracts = (params?: GetContractsParams) =>
  request.api.get<ContractListItem[], BaseApiResponse<void>>(
    PATHS.getContracts,
    params
  )

export const getPendingContracts = (team?: string | null) =>
  request.api.get<ContractListItem[]>(
    PATHS.getPendingContracts,
    team ? { team } : undefined
  )

export const getContractInitialValues = (leadID: string) =>
  request.api.get<BaseApiResponse<unknown>>(
    PATHS.getContractInitialValues(leadID)
  )

export const getContractById = (id: string) =>
  request.api.get<Contract>(PATHS.contract(id).get)

export const createContract = (data: ContractRequestData) =>
  request.api.post<BaseApiResponse<Contract>>(PATHS.createContract, data)

export const previewContract = (data: ContractRequestData) =>
  request.api.post<string>(PATHS.previewContract, data)

export const generateContract = (data: ContractRequestData) =>
  request.api.post<BaseApiResponse<Contract>>(PATHS.generateContract, data)

export const saveContractFile = (id: string) =>
  request.api.post<BaseApiResponse<null>>(PATHS.contract(id).save)

export const updateContract = (id: string, data: ContractRequestData) =>
  request.api.patch<BaseApiResponse<Contract>>(PATHS.contract(id).update, data)

export const submitForReview = ({
  contractId,
  attachmentId: attachment_id
}: SubmitForReviewData) =>
  announcesApprovalChange(
    request.api.post<BaseApiResponse<LeadContract>>(
      PATHS.submitForReview(contractId),
      { attachment_id }
    )
  )

export const rejectContract = ({ id, reason }: RejectContractData) =>
  announcesApprovalChange(
    request.api.post<BaseApiResponse<null>>(PATHS.contract(id).reject, {
      reason
    })
  )

export const cancelContract = ({ id, reason, notes }: CancelContractData) =>
  announcesApprovalChange(
    request.api.post<BaseApiResponse<null>>(PATHS.contract(id).cancel, {
      reason,
      notes
    })
  )

export const approveContract = (contractId: string) =>
  announcesApprovalChange(
    request.api.post<BaseApiResponse<null>>(PATHS.contract(contractId).approve)
  )

export const uploadCompanyLogo = (logo: unknown) => {
  const headers = { "Content-Type": "multipart/form-data" }
  const formData = createFormDataNested(logo, "file")

  return request.api.post<BaseApiResponse<unknown>>(
    PATHS.uploadCompanyLogo,
    formData,
    { headers }
  )
}

export const duplicateContract = (contractId: string) =>
  request.api.post<BaseApiResponse<LeadContract>>(
    PATHS.contract(contractId).duplicate
  )

export const uploadContract = (
  data: UploadContractData,
  options: UploadContractOptions = {}
) => {
  const headers = { "Content-Type": "multipart/form-data" }
  const formData = createFormData(data, "file")

  return request.api.post<BaseApiResponse<LeadContract>>(
    PATHS.uploadContractFile,
    formData,
    { headers, ...options }
  )
}

const sentContractToClientRequest = (contractId: string) =>
  request.api.post<BaseApiResponse<LeadContract>>(
    PATHS.contract(contractId).sentToClient
  )

const sendContractEmailRequest = (contractId: string) =>
  request.api.post<BaseApiResponse<LeadContract>>(
    PATHS.contract(contractId).sendEmail
  )

export const getFilterOptions = () =>
  request.api.get<ContractFilterOptions>(PATHS.filterOptions)

export const sentContractToClient = (contractId: string) =>
  afterStatusWrite(sentContractToClientRequest(contractId))

export const sendContractEmail = (contractId: string) =>
  afterStatusWrite(sendContractEmailRequest(contractId))
